/**
 * Dead Zone Detector — Identifies boring, redundant, or unnecessary stretches of the script.
 * Part of the Greenlight Engine for 1895.ai.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, ChevronDown, ChevronUp, Clock, MapPin } from 'lucide-react';

const TYPE_LABELS = {
  emotional_flatline: 'Emotional Flatline',
  redundant_exposition: 'Redundant Exposition',
  stalled_plot: 'Stalled Plot',
  character_holding: 'Character Holding Pattern',
  dialogue_spinning: 'Dialogue Wheel-Spinning',
  tonal_monotone: 'Tonal Monotone',
  missing_stakes: 'Missing Stakes Escalation',
};

const SEVERITY_STYLES = {
  critical: {
    border: 'border-red-500/30',
    bg: 'bg-red-500/5',
    badge: 'bg-red-500/15 text-red-400',
    icon: 'text-red-400',
  },
  moderate: {
    border: 'border-orange-500/30',
    bg: 'bg-orange-500/5',
    badge: 'bg-orange-500/15 text-orange-400',
    icon: 'text-orange-400',
  },
  minor: {
    border: 'border-yellow-500/30',
    bg: 'bg-yellow-500/5',
    badge: 'bg-yellow-500/15 text-yellow-400',
    icon: 'text-yellow-400',
  },
};

function scoreColor(score) {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-amber-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
}

function DeadZoneCard({ zone, isExpanded, onToggle }) {
  const style = SEVERITY_STYLES[zone.severity] || SEVERITY_STYLES.minor;
  const label = TYPE_LABELS[zone.type] || zone.type.replace(/_/g, ' ');

  return (
    <div className={`rounded-lg border ${style.border} ${style.bg}`}>
      {/* Collapsed header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 text-left"
      >
        <AlertTriangle size={14} className={style.icon} />
        <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${style.badge}`}>
          {label}
        </span>
        <span className="text-[11px] text-[var(--text-muted)] flex items-center gap-1">
          <MapPin size={10} />
          pp. {zone.page_start}–{zone.page_end}
        </span>
        <span className="flex-1 text-xs text-[var(--text-secondary)] truncate">
          {zone.description}
        </span>
        {isExpanded
          ? <ChevronUp size={14} className="text-[var(--text-muted)] shrink-0" />
          : <ChevronDown size={14} className="text-[var(--text-muted)] shrink-0" />
        }
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-3 border-t border-[var(--border-subtle)]">
              {/* Full description */}
              <p className="text-xs text-[var(--text-secondary)] pt-3">
                {zone.description}
              </p>

              {/* Scene range */}
              <div className="flex items-center gap-2 text-[11px] text-[var(--text-muted)]">
                <Clock size={10} />
                <span>Scenes {zone.scene_start}–{zone.scene_end}</span>
                <span className="text-[var(--border-subtle)]">|</span>
                <span>Pages {zone.page_start}–{zone.page_end} ({zone.page_end - zone.page_start + 1} pages)</span>
              </div>

              {/* Suggestions */}
              {zone.suggestions?.length > 0 && (
                <div className="space-y-1.5">
                  <h5 className="text-[11px] font-semibold text-[var(--text-secondary)]">Suggestions</h5>
                  <ol className="space-y-1 list-none">
                    {zone.suggestions.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-[11px] text-[var(--text-secondary)]">
                        <span className="text-[var(--text-muted)] font-mono shrink-0">{i + 1}.</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Affected characters */}
              {zone.affected_characters?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] text-[var(--text-muted)]">Affected:</span>
                  {zone.affected_characters.map((c, i) => (
                    <span
                      key={i}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg-app)] text-[var(--text-secondary)] border border-[var(--border-subtle)]"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function DeadZoneDetector({ data }) {
  const [expandedIndex, setExpandedIndex] = useState(null);

  if (!data) return null;

  const {
    dead_zones = [],
    total_dead_pages,
    dead_zone_percentage,
    pacing_score,
    pacing_assessment,
  } = data;

  const handleToggle = (i) => {
    setExpandedIndex(expandedIndex === i ? null : i);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Dead Zone Analysis</h3>
        {pacing_score != null && (
          <span className={`text-lg font-bold ${scoreColor(pacing_score)}`}>{pacing_score}/100</span>
        )}
      </div>

      {/* Summary stats */}
      <div className="flex gap-3">
        <div className="flex-1 p-3 rounded-lg bg-[var(--bg-app)] text-center">
          <div className="text-lg font-bold text-[var(--text-primary)]">{total_dead_pages ?? '—'}</div>
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">Dead Pages</div>
        </div>
        <div className="flex-1 p-3 rounded-lg bg-[var(--bg-app)] text-center">
          <div className="text-lg font-bold text-[var(--text-primary)]">
            {dead_zone_percentage != null ? `${dead_zone_percentage}%` : '—'}
          </div>
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">Dead Zone %</div>
        </div>
        <div className="flex-1 p-3 rounded-lg bg-[var(--bg-app)] text-center">
          <div className="text-lg font-bold text-[var(--text-primary)]">{dead_zones.length}</div>
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">Zones Found</div>
        </div>
      </div>

      {/* Severity breakdown */}
      <div className="flex gap-3 text-xs">
        <span className="text-red-400">
          {dead_zones.filter((z) => z.severity === 'critical').length} Critical
        </span>
        <span className="text-orange-400">
          {dead_zones.filter((z) => z.severity === 'moderate').length} Moderate
        </span>
        <span className="text-yellow-400">
          {dead_zones.filter((z) => z.severity === 'minor').length} Minor
        </span>
      </div>

      {/* Dead zone cards */}
      <div className="space-y-2">
        {dead_zones.map((zone, i) => (
          <DeadZoneCard
            key={i}
            zone={zone}
            isExpanded={expandedIndex === i}
            onToggle={() => handleToggle(i)}
          />
        ))}
      </div>

      {/* Pacing assessment */}
      {pacing_assessment && (
        <p className="text-xs text-[var(--text-secondary)] p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)]">
          {pacing_assessment}
        </p>
      )}
    </div>
  );
}
